import { NextRouter } from 'next/router';
import { useEffect } from 'react';

import { ThemeProvider } from '@emotion/react';
import { setHeadBaseUrl } from '@otovo/shared/components/Head/headContext';
import NextPageRoot from '@otovo/shared/components/NextPageRoot';
import { useAnalytics } from '@otovo/shared/hooks/useAnalytics';
import UtmLocalStorage from '@otovo/shared/lib/analytics/queryParams/UtmLocalStorage';
import resolveTagManagerID from '@otovo/shared/lib/analytics/resolveTagManagerID';
import { refreshWidget } from '@otovo/shared/lib/chatUtils';
import GoogleTagManagerAndCookieConsent from '@otovo/shared/lib/consent/GoogleTagManagerAndCookieConsent';

import loglevelPlugin from '@otovo/shared/lib/sentry/loglevelPlugin';
import throwForFalsy from '@otovo/shared/lib/throwForFalsy';

import workaroundBrowserExtensions from '@otovo/shared/utils/workaroundBrowserExtensions';

import { NextProps } from '@otovo/shared/lib/next/types';
import { decorateJSONTheme } from '@otovo/shared/rainbow/theme';
import '../styles/tailwindConfig.css';

type Props = {
  Component: any;
  pageProps: NextProps;
  router: NextRouter;
  err: any;
};

/**
 * Next.js uses the App component to initialize pages.
 * You can override it and control the page initialization. Which allows you to do amazing things like
 *  - Persisting layout between page changes
 *  - Keeping state when navigating pages
 *  - Custom error handling using componentDidCatch
 *  - Inject additional data into pages
 *  - Add global CSS
 */

function BreezeApp({ Component, pageProps, router, err }: Props) {
  // `err` is a workaround for https://github.com/vercel/next.js/issues/8592
  const { asPath, query } = router;
  const { config } = pageProps;

  const gtmId = resolveTagManagerID(config);

  useEffect(() => {
    const handleRouteChange = () => {
      refreshWidget();
    };

    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    loglevelPlugin.initialize();
  });

  useAnalytics({ path: asPath, isGTMEnabled: !!gtmId });

  useEffect(() => {
    if (/Chrom/.test(window.navigator.userAgent)) {
      workaroundBrowserExtensions();
    }
  }, []);

  useEffect(() => {
    UtmLocalStorage.store(query);
  }, [query]);

  setHeadBaseUrl(throwForFalsy(process.env.NEXT_PUBLIC_BREEZE_URL));

  // See search-ssr.js for more details
  const doNotTrack = query.dnt === 'true';

  decorateJSONTheme(config.theme);

  return (
    // breeze uses @otovo/rainbow components that expects an Empotion theme.
    // TODO: Remove when Rainbow is removed from Breeze
    <ThemeProvider theme={config.theme}>
      <NextPageRoot configValues={config}>
        {!!gtmId && !doNotTrack && (
          <GoogleTagManagerAndCookieConsent
            googleTagManagerId={gtmId}
            query={asPath.split('?')[1]}
          />
        )}
        <Component {...pageProps} err={err} />
        <div id="modal-root" />
      </NextPageRoot>
    </ThemeProvider>
  );
}

export default BreezeApp;
